'use strict';

window.BASE_URL = 'https://api.thenexvolt.com/api/v2';
// window.BASE_URL = 'http://192.168.1.146:3001/api/v2';

function normalizeMoney(value, fallback = 0) {
  const numericValue = Number(value ?? fallback);
  return Number.isFinite(numericValue) ? numericValue / 100 : fallback / 100;
}

function normalizePriceCents(value) {
  return normalizeMoney(value, 0);
}

function getShopifyCurrency() {
  return window.Shopify?.currency?.active || 'USD';
}

function getProductVariantId(product) {
  return product?.variants?.[0]?.id || product?.id || '';
}

class FacebookPixel {
  initializedPixels;
  sdkVersion;
  sdkUrl;
  fbGraphAPIUrl;
  LOAD_TIMEOUT;
  sdkLoadPromise;
  constructor() {
    this.initializedPixels = new Set(); // Track initialized pixels
    this.fbGraphAPIUrl = 'https://www.facebook.com/tr/';
    this.sdkUrl = 'https://connect.facebook.net/en_US/fbevents.js';
    this.sdkVersion = '2.9.159';
    this.sdkLoadPromise = null;
    this.LOAD_TIMEOUT = 3000;
  }
  async loadSdk() {
    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // If fbq already loaded (e.g., from GTM)
    if (typeof window.fbq !== 'undefined' && window.fbq.loaded) {
      this.sdkLoadPromise = Promise.resolve();
      return this.sdkLoadPromise;
    }

    const sdkUrl = this.sdkUrl;
    const loadTimeout = this.LOAD_TIMEOUT;

    this.sdkLoadPromise = new Promise((resolve) => {
      let isResolved = false;
      let timeoutId;

      try {
        // Initialize fbq stub function
        ((f, b, e, v) => {
          if (f.fbq && f.fbq.loaded) {
            if (!isResolved) {
              isResolved = true;
              clearTimeout(timeoutId);
              resolve();
            }
            return;
          }

          const n = (f.fbq = (...args) => {
            if (n.callMethod) {
              n.callMethod(...args);
              return;
            }

            n.queue.push(args);
          });

          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = '2.0';
          n.queue = [];

          // Create script element
          const script = b.createElement(e);
          script.async = true;
          script.src = v;

          script.onload = () => {
            if (!isResolved) {
              isResolved = true;
              clearTimeout(timeoutId);
              resolve();
            }
          };

          script.onerror = () => {
            if (!isResolved) {
              isResolved = true;
              clearTimeout(timeoutId);
              // Don't reject - fbq stub is already working
              resolve();
            }
          };

          // Insert script
          const firstScript = b.getElementsByTagName(e)[0];
          if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
          } else {
            b.head.appendChild(script);
          }
        })(window, document, 'script', sdkUrl);

        // Timeout fallback
        timeoutId = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            resolve();
          }
        }, loadTimeout);
      } catch (error) {
        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeoutId);
          resolve();
        }
      }
    });

    return this.sdkLoadPromise;
  }
  async initPixel(pixelId) {
    if (!pixelId) {
      return;
    }

    // Check if pixel already initialized
    if (this.initializedPixels.has(pixelId)) {
      return;
    }

    try {
      await this.loadSdk();
      if (typeof window === 'undefined' || !window.fbq) return;

      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
      this.initializedPixels.add(pixelId);
      await this.waitForFacebookCookies();
    } catch {}
  }
  async initPixels(pixels) {
    if (!pixels?.length) return;
    const uniquePixels = Array.from(new Set(pixels.map((item) => item.pixel_id)));
    await Promise.all(uniquePixels.map((item) => this.initPixel(item)));
  }
  async waitForFacebookCookies() {
    const maxAttempts = 20; // Maximum 2 seconds (20 * 100ms)
    for (let attemptCount = 0; attemptCount <= maxAttempts; attemptCount += 1) {
      const fbp = this.getCookie('_fbp');

      // _fbp is the primary cookie, _fbc is optional (only set when coming from FB)
      if (fbp || attemptCount >= maxAttempts) {
        if (fbp) {
          this.setStorage('fbp', fbp);
        }

        const fbc = this.getCookie('_fbc');
        if (fbc) {
          this.setStorage('fbc', fbc);
        }

        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }
  getCookie(name) {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }
  setStorage(key, value) {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch (e) {}
  }
  getLStorage(key) {
    if (typeof localStorage === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  addBrowserData(data) {
    if (typeof navigator !== 'undefined') {
      data.ua = navigator.userAgent;
    }

    if (typeof window !== 'undefined' && window.screen) {
      data.sw = window.screen.width;
      data.sh = window.screen.height;
      data.vw = window.innerWidth || document.documentElement.clientWidth;
      data.vh = window.innerHeight || document.documentElement.clientHeight;
    }

    data.tz = new Date().getTimezoneOffset();
  }
  addEventData(data, eventData) {
    const fieldMap = {
      content_ids: 'content_ids',
      content_type: 'content_type',
      content_name: 'content_name',
      value: 'value',
      currency: 'currency',
      quantity: 'num_items',
    };

    for (const key in eventData) {
      if (eventData.hasOwnProperty(key) && eventData[key] !== undefined) {
        const paramName = fieldMap[key] || key;
        let value = eventData[key];

        if (Array.isArray(value)) {
          value = JSON.stringify(value);
        } else if (typeof value !== 'string') {
          value = String(value);
        }

        data['cd[' + paramName + ']'] = value;
      }
    }
  }
  async sendViaFetch(url) {
    try {
      await fetch(url, {
        method: 'GET',
        keepalive: true,
      });
    } catch {}
  }

  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Get cart error!');
    return response.json();
  }
  async sendEvent(pixelId, eventName, eventData, eventId) {
    if (!this.getCookie('_fbc')) return;
    try {
      const dataPost = {
        id: pixelId,
        ev: eventName,
        ts: Math.floor(Date.now() / 1000),
        dl: window.location.href,
        rl: (typeof document !== 'undefined' && document.referrer) || window.location.origin,
        v: this.sdkVersion,
        fbp: this.getCookie('_fbp') || this.getLStorage('fbp') || '',
        fbc: this.getCookie('_fbc') || this.getLStorage('fbc') || '',
        ...(eventId ? { eid: eventId } : {}),
      };

      this.addBrowserData(dataPost);
      this.addEventData(dataPost, eventData || {});

      // Send via fetch or image fallback
      const params = new URLSearchParams(dataPost);
      const pixelUrl = this.fbGraphAPIUrl + '?' + params.toString();
      this.sendViaFetch(pixelUrl);

      return;
    } catch {}
  }
  async viewContent(_product) {
    if (typeof window !== 'undefined' && window.fbq) {
      const dataPost = {
        content_name: _product?.title || '',
        content_brand: _product?.vendor || '',
        shop_name: window.Shopify?.shop || '',
        content_category: _product?.type || '',
        content_ids: [_product?.id],
        content_type: 'product',
        value: normalizePriceCents(_product?.price),
        currency: getShopifyCurrency(),
      };
      const pixels = [...this.initializedPixels];
      await Promise.all(
        pixels.map((pixel) => this.sendEvent(pixel, 'ViewContent', dataPost, window.__sessionId))
      );
    }
  }
  async addToCart(_product) {
    if (typeof window !== 'undefined' && window.fbq) {
      const cart = await this.getCart();
      const dataPost = {
        content_ids: [_product?.id],
        content_name: _product?.title || '',
        content_type: 'product',
        value: normalizePriceCents(_product?.price),
        currency: getShopifyCurrency(),
        quantity: _product?.quantity || 1,
      };
      const pixels = [...this.initializedPixels];
      await Promise.all(
        pixels.map((pixel) => this.sendEvent(pixel, 'AddToCart', dataPost, cart.token))
      );
    }
  }
  async initCheckout(cart) {
    if (typeof window !== 'undefined' && window.fbq) {
      const dataPost = {
        content_type: 'product',
        shop_name: window.Shopify?.shop || '',
        content_ids: cart?.items?.map((item) => item.variantId || item.productId).join(','),
        content_brand: cart?.items?.map((item) => item.vendor).join(','),
        contents: cart?.items?.map((item) => ({
          id: item.variantId || item.productId,
          quantity: item.quantity,
          item_price: item.price,
        })),
        currency: cart?.currency || getShopifyCurrency(),
        num_items: cart?.totalItems || 0,
        value: normalizeMoney(cart?.totalPrice),
      };
      const { token } = await this.getCart();
      const pixels = [...this.initializedPixels];
      await Promise.all(
        pixels.map((pixel) => this.sendEvent(pixel, 'InitiateCheckout', dataPost, token))
      );
    }
  }
}
class GoogleAds {
  analyticIds = [];
  conversions = [];

  init(analyticIds = [], conversions = []) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.id = 'x-gtag';
      script.src = `https://www.googletagmanager.com/gtag/js?id=${analyticIds[0] || conversions[0]}`;
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag =
        window.gtag ||
        function () {
          window.dataLayer.push(arguments);
        };

      window.gtag('js', new Date());
      analyticIds?.forEach((item) => {
        window.gtag('config', item);
      });

      this.analyticIds = analyticIds || [];
      this.conversions = conversions || [];

      this.conversions?.forEach((item) => {
        window.gtag('config', item, { allow_enhanced_conversions: true });
      });
    });
  }

  send(name, data) {
    if (typeof window !== 'undefined' && window?.gtag) {
      window.gtag('event', name, data);
      console.log(`Google Analytic Action "${name}" triggered with data:`, data);
    } else {
      console.warn('Google Analytic Action is not initialized or running on the server-side.');
    }
  }

  viewContent(_product) {
    if (!_product) return;

    const productId = _product.id || _product.handle;
    const priceValue = normalizePriceCents(_product?.price);
    const currency = getShopifyCurrency();

    this.send('page_view', {
      page_path: window.location.pathname,
      send_to: this.analyticIds.join(','),
    });
    this.send('view_item', {
      items: [
        {
          google_business_vertical: 'retail',
          id: productId,
        },
      ],
      value: priceValue,
      send_to: this.analyticIds.join(','),
    });
    this.send('view_item', {
      currency,
      eventName: 'view_item',
      shop_id: window.shop?.id || '',
      shop_name: window.shop?.name || '',
      items: [
        {
          id: productId,
          name: _product.title,
          price: priceValue,
          brand: _product?.vendor || '',
          category: '',
          item_id: productId,
          item_name: _product.title,
          item_brand: _product?.vendor || '',
          item_category: '',
        },
      ],
    });
  }

  addToCart(_product) {
    if (!_product) return;

    const productId = _product.id || _product.handle;
    const priceValue = normalizePriceCents(_product?.price);
    const currency = getShopifyCurrency();

    this.send('add_to_cart', {
      currency,
      eventName: 'add_to_cart',
      shop_id: window.shop?.id || '',
      shop_name: window.shop?.name || '',
      send_to: this.analyticIds.join(','),
      value: priceValue,
      items: [
        {
          id: productId,
          name: _product.title,
          price: priceValue,
          brand: _product?.vendor || '',
          category: '',
          item_id: productId,
          item_name: _product.title,
          item_brand: _product?.vendor || '',
          item_category: '',
          quantity: _product.quantity,
        },
      ],
    });
    this.send('add_to_cart', {
      items: [
        {
          google_business_vertical: 'retail',
          id: productId,
        },
      ],
      value: priceValue,
      send_to: this.analyticIds.join(','),
    });
  }

  initCheckout(cart) {
    if (!cart) return;

    const currency = cart.currency || getShopifyCurrency();
    const items = Array.isArray(cart.items) ? cart.items : [];

    this.send('begin_checkout', {
      currency,
      value: normalizeMoney(cart.totalPrice),
      send_to: this.analyticIds.join(','),
      items: items.map((item) => ({
        google_business_vertical: 'retail',
        id: item.variantId || item.productId,
      })),
    });
    this.send('begin_checkout', {
      currency,
      eventName: 'begin_checkout',
      shop_id: window.shop?.id || '',
      shop_name: window.shop?.name || '',
      send_to: this.analyticIds.join(','),
      value: normalizeMoney(cart.totalPrice),
      items: items.map((item) => ({
        id: item.variantId || item.productId,
        name: item.title,
        price: normalizeMoney(item.unitPrice),
        brand: '',
        category: '',
        quantity: item.quantity,
        item_id: item.variantId || item.productId,
        item_name: item.title,
        item_brand: '',
        item_category: '',
        item_variant: item.variantId || item.productId,
      })),
    });
  }
}
class TiktokPixel {
  constructor() {}
  init(pixels) {
    !(function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = (w[t] = w[t] || []);
      ((ttq.methods = [
        'page',
        'track',
        'identify',
        'instances',
        'debug',
        'on',
        'off',
        'once',
        'ready',
        'alias',
        'group',
        'enableCookie',
        'disableCookie',
        'holdConsent',
        'revokeConsent',
        'grantConsent',
      ]),
        (ttq.setAndDefer = function (t, e) {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }));
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ((ttq.instance = function (t) {
        for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
          ttq.setAndDefer(e, ttq.methods[n]);
        return e;
      }),
        (ttq.load = function (e, n) {
          var i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ((ttq._i = ttq._i || {}),
            (ttq._i[e] = []),
            (ttq._i[e]._u = i),
            (ttq._t = ttq._t || {}),
            (ttq._t[e] = +new Date()),
            (ttq._o = ttq._o || {}),
            (ttq._o[e] = n || {}));
          var o = document.createElement('script');
          ((o.type = 'text/javascript'), (o.async = !0), (o.src = i + '?sdkid=' + e + '&lib=' + t));
          var a = document.getElementsByTagName('script')[0];
          a.parentNode.insertBefore(o, a);
        }));
      pixels.forEach((item) => {
        ttq.load(item.id);
      });
      ttq.page();
    })(window, document, 'ttq');
  }
  send(name, data, id) {
    if (typeof window !== 'undefined' && window.ttq) {
      window.ttq.track(name, data, {
        event_id: id,
      });
      console.log(`Tiktok Action "${name}" triggered with data:`, {
        data,
        event_id: id,
      });
    } else {
      console.warn('Tiktok Action is not initialized or running on the server-side.');
    }
  }
  viewContent(_product) {
    if (!_product) return;
    const productId = _product.id || _product.handle;
    this.send('ViewContent', {
      contents: [
        {
          content_id: productId,
          content_type: 'product',
          content_name: _product?.title || '',
        },
      ],
      value: String(normalizePriceCents(_product?.price)),
      currency: getShopifyCurrency(),
    });
  }
  addToCart(_product) {
    if (!_product) return;
    const productId = _product.id || _product.handle;
    this.send('AddToCart', {
      contents: [
        {
          content_id: productId,
          content_type: 'product',
          content_name: _product?.title || '',
        },
      ],
      quantity: _product?.quantity || 1,
      value: String(normalizePriceCents(_product?.price)),
      currency: getShopifyCurrency(),
    });
  }
  initCheckout(cart) {
    if (!cart) return;
    this.send(
      'PlaceAnOrder',
      {
        currency: cart.currency || getShopifyCurrency(),
        content_type: 'product',
        contents: cart?.items?.map((item) => ({
          id: item.variantId || item.productId,
          quantity: item.quantity,
          item_price: item.unitPrice,
          content_id: item.variantId || item.productId,
          content_name: item.variantTitle,
          content_type: 'product',
        })),
        value: normalizeMoney(cart.totalPrice),
      },
      cart._id
    );
  }
}
class Pinterest {
  constructor() {}
  init(tags) {
    !(function (e) {
      if (!window.pintrk) {
        window.pintrk = function () {
          window.pintrk.queue.push(Array.prototype.slice.call(arguments));
        };
        var n = window.pintrk;
        ((n.queue = []), (n.version = '3.0'));
        var t = document.createElement('script');
        ((t.async = !0), (t.src = e));
        var r = document.getElementsByTagName('script')[0];
        r.parentNode.insertBefore(t, r);
      }
    })('https://s.pinimg.com/ct/core.js');
    tags.forEach((item) => {
      pintrk('load', item.id, { em: item.email });
    });
    pintrk('page');
  }
  send(name, data) {
    if (typeof window !== 'undefined' && window.pintrk) {
      window.pintrk('track', name, data);
      console.log(`Pinterest Tag "${name}" triggered`, data);
    } else {
      console.warn('Pinterest Tag not initialized or running server-side.');
    }
  }
  viewContent(_product) {
    if (!_product) return;
    const priceValue = normalizePriceCents(_product?.price);
    this.send('pagevisit', {
      value: priceValue,
      currency: getShopifyCurrency(),
      line_items: [
        {
          product_id: _product?.id || _product?.handle,
          product_name: _product.title,
          product_price: priceValue,
          product_quantity: 1,
        },
      ],
    });
  }
  addToCart(_product) {
    if (!_product) return;
    const priceValue = normalizePriceCents(_product?.price);
    this.send('addtocart', {
      event_id: _product?.id || _product?.handle,
      value: priceValue,
      currency: getShopifyCurrency(),
      order_quantity: _product.quantity || 1,
      line_items: [
        {
          product_id: _product?.id || _product?.handle,
          product_name: _product.title,
          product_price: priceValue,
          product_quantity: _product.quantity || 1,
        },
      ],
    });
  }
  initCheckout(cart) {
    if (!cart) return;
    this.send('checkout', {
      value: normalizeMoney(cart?.totalPrice),
      currency: cart.currency || getShopifyCurrency(),
      order_quantity: cart.totalItems,
      item_count: cart.totalItems,
      event_id: cart?._id,
      line_items: (cart.items || []).map((item) => ({
        product_name: item.variantTitle,
        product_id: item.variantId || item.productId,
        product_price: normalizeMoney(item.unitPrice),
        product_quantity: item.quantity,
      })),
    });
  }
}
class SnapChatPixel {
  constructor() {}
  init(pixels) {
    (function (e, t, n) {
      if (e.snaptr) return;
      var a = (e.snaptr = function () {
        a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments);
      });
      a.queue = [];
      var s = 'script';
      r = t.createElement(s);
      r.async = !0;
      r.src = n;
      var u = t.getElementsByTagName(s)[0];
      u.parentNode.insertBefore(r, u);
    })(window, document, 'https://sc-static.net/scevent.min.js');
    pixels.forEach((item) => {
      snaptr('init', item.id, { user_email: item.email });
    });
    snaptr('track', 'PAGE_VIEW');
  }
  send(name, data, eventID = '') {
    if (typeof window !== 'undefined' && window.snaptr) {
      window.snaptr('track', name, data, { eventID });
      console.log(`Snap Pixel "${name}" triggered`, { data });
    } else {
      console.warn('Snap Pixel not initialized or running server-side.');
    }
  }
  viewContent(_product) {
    if (!_product) return;
    this.send(
      'VIEW_CONTENT',
      {
        item_ids: [_product?.id || _product?.handle],
        price: normalizePriceCents(_product?.price),
        currency: getShopifyCurrency(),
        item_name: _product?.title,
        item_brand: _product?.vendor || '',
        item_type: 'product',
      },
      window.__sessionId
    );
  }
  addToCart(_product) {
    if (!_product) return;
    this.send(
      'ADD_CART',
      {
        item_ids: [_product?.id || _product?.handle],
        price: normalizePriceCents(_product?.price),
        currency: getShopifyCurrency(),
        item_name: _product?.title || '',
        number_items: _product.quantity || 1,
      },
      _product?.id || _product?.handle
    );
  }
  initCheckout(cart) {
    if (!cart) return;
    this.send(
      'START_CHECKOUT',
      {
        item_ids: (cart.items || []).map((item) => item.variantId || item.productId),
        price: normalizeMoney(cart?.totalPrice),
        currency: cart.currency || getShopifyCurrency(),
        number_items: cart?.totalItems,
        item_type: 'product',
        items: cart?.items?.map((item) => ({
          id: item.variantId || item.productId,
          quantity: item.quantity,
          item_price: normalizeMoney(item.price),
        })),
      },
      cart._id
    );
  }
}
class NewsbreakPixel {
  constructor() {}
  init(pixels) {
    (!(function (e, n, t, s, c, i, a) {
      e[s] ||
        (((c = e[s] =
          function () {
            c.process ? c.process.apply(c, arguments) : c.queue.push(arguments);
          }).queue = []),
        (c.t = +new Date()),
        ((i = n.createElement(t)).async = 1),
        (i.src =
          'https://static.newsbreak.com/business/tracking/nbpixel.js?t=' +
          864e5 * Math.ceil(new Date() / 864e5)),
        (a = n.getElementsByTagName(t)[0]).parentNode.insertBefore(i, a));
    })(window, document, 'script', 'nbpix'),
      pixels.forEach((item) => {
        nbpix('init', `ID-${item}`);
      }));
    nbpix('event', 'pageload');
  }
  send(name, data) {
    if (typeof window !== 'undefined' && window?.nbpix) {
      window.nbpix('event', name, data);
      console.log(`Newsbreak Action "${name}" triggered with data:`, data);
    } else {
      console.warn('Newsbreak Action is not initialized or running on the server-side.');
    }
  }
  viewContent(_product) {
    if (!_product) return;
    this.send('view_content', {
      item_id: _product.id || _product.handle,
      item_name: _product.title || '',
      nb_value: normalizePriceCents(_product?.price),
      currency: getShopifyCurrency(),
      page_url: window.location.href,
    });
  }
  addToCart(_product) {
    if (!_product) return;
    this.send('add_to_cart', {
      item_id: _product.id || _product.handle,
      item_name: _product.title,
      nb_value: normalizePriceCents(_product?.price),
      currency: getShopifyCurrency(),
      quantity: _product?.quantity,
      cart_id: _product.id || _product.handle,
    });
  }
  initCheckout(cart) {
    this.send('initiate_checkout', {
      currency: cart.currency,
      nb_value: cart.totalPrice,
      num_items: cart.totalItems,
      items: cart.items.map((item) => ({
        content_id: item.variantId || item.productId,
        content_name: item.variantTitle,
        price: item.price / 100,
        quantity: item.quantity,
      })),
      cart_id: cart._id,
    });
  }
}
class Checkout {
  constructor() {
    this.overlay = document.getElementById('checkout-overlay');
  }
  showOverlay() {
    this.overlay.style.display = 'flex';
  }
  hideOverlay() {
    this.overlay.style.display = 'none';
  }
  async getProxies() {
    try {
      const response = await fetch(`${window.BASE_URL}/proxies`);
      if (!response.ok) throw new Error('Get proxies error!');
      const { data } = await response.json();
      this.proxies = data?.proxies || [];
      this.proxyGroupId = data?.proxyGroupId || '';
      const paypalProxy = this.proxies.find((item) => item.proxyType === 'PAYPAL');
      if (!paypalProxy) {
        document.getElementById('paypal-product').remove();
      }
    } catch (error) {
      console.log('error: ', error);
    }
  }
  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Get cart error!');
    return response.json();
  }
  async postAction(cart, url) {
    try {
      await fetch(`${window.BASE_URL}/analytics/event`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shop: window.Shopify?.shop || '',
          visitorId: window.__visitorId,
          sessionId: window.__sessionId,
          eventType: 'checkout',
          checkoutId: cart._id,
          cartId: cart._id,
          url,
          storeType: 'SHOPIFY',
        }),
      });
      await window.adsTracking.facebook.initCheckout(cart);
      window.adsTracking.google.initCheckout(cart);
      window.adsTracking.tiktok.initCheckout(cart);
      window.adsTracking.pinterest.initCheckout(cart);
      window.adsTracking.snapchat.initCheckout(cart);
      window.adsTracking.newsbreak.initCheckout(cart);
    } catch (error) {
      console.log('error: ', error);
    }
  }
  async buyNow() {
    try {
      this.showOverlay();

      const cart = await this.getCart();
      if (!cart?.token) throw new Error('Cart token missing');

      const dataPost = {
        shop: window.Shopify?.shop || '',
        cartId: cart.token,
        store: {
          ...(window.shop || {}),
          trackingData: getTrackingData(),
        },
      };

      const response = await fetch(`${window.BASE_URL}/carts`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataPost),
      });

      if (!response.ok) throw new Error(response.statusText);

      const { data, success } = await response.json();
      if (!success || !data?._id) throw new Error('Checkout creation failed');

      await this.postAction(data, `/a/ck/checkout/${data._id}`);
      window.location.href = `/a/ck/checkout/${data._id}`;
    } catch (error) {
      console.log('error: ', error);
    } finally {
      this.hideOverlay();
    }
  }
}
class Visitor {
  getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
  }

  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax;Secure`;
  }

  getSessionFromStorage() {
    try {
      return localStorage.getItem('_sess_pub');
    } catch (error) {
      console.error('Error getting session from localStorage:', error);
      return null;
    }
  }

  setSessionToStorage(sessionId) {
    try {
      localStorage.setItem('_sess_pub', sessionId);
    } catch (error) {
      console.error('Error setting session to localStorage:', error);
    }
  }

  generateUUID() {
    return (
      crypto.randomUUID?.() ||
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      })
    );
  }

  applyIdentity(visitorId, sessionId) {
    window.__visitorId = visitorId; // Persist ~1 year
    window.__sessionId = sessionId; // Infinite persistence
  }

  async getVisitor() {
    try {
      let visitorId = this.getCookie('_anon_pub');
      let sessionId = this.getSessionFromStorage() || `sess_${this.generateUUID()}`;

      if (!this.getSessionFromStorage()) {
        this.setSessionToStorage(sessionId);
      }

      if (!visitorId) {
        visitorId = this.generateUUID();
        this.setCookie('_anon_pub', visitorId, 365);
      }

      this.applyIdentity(visitorId, sessionId);
      return { visitorId, sessionId };
    } catch (error) {
      console.error('Error in getVisitor:', error);
      const visitorId = this.generateUUID();
      const sessionId = this.getSessionFromStorage() || `sess_${this.generateUUID()}`;
      this.setCookie('_anon_pub', visitorId, 365);
      this.setSessionToStorage(sessionId);
      this.applyIdentity(visitorId, sessionId);
      return { visitorId, sessionId };
    }
  }

  constructor() {
    // Keep constructor side-effect free; ensure identity is initialized before tracking.
  }
}

async function postAction() {
  try {
    if (!window.__visitorId || !window.__sessionId) {
      await window.visitor.getVisitor();
    }

    const dataPost = {
      shop: window.Shopify?.shop || '',
      eventType: 'view',
      visitorId: window.__visitorId,
      sessionId: window.__sessionId,
      url: window.location.href,
      storeType: 'SHOPIFY',
    };
    await fetch(`${window.BASE_URL}/analytics/event`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataPost),
    });
  } catch (error) {
    console.log('error: ', error);
  }
}
async function postActionProductPage() {
  try {
    if (!window.__visitorId || !window.__sessionId) {
      await window.visitor.getVisitor();
    }

    if (!product) return;

    const dataPost = {
      shop: window.Shopify?.shop || '',
      eventType: 'product_view',
      visitorId: window.__visitorId,
      sessionId: window.__sessionId,
      url: window.location.href,
      productId: String(product?.id || ''),
      variantId: String(getProductVariantId(product) || ''),
      storeType: 'SHOPIFY',
    };
    await fetch(`${window.BASE_URL}/analytics/event`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataPost),
    });
    await window.adsTracking.facebook.viewContent(product);
    window.adsTracking.google.viewContent(product);
    window.adsTracking.tiktok.viewContent(product);
    window.adsTracking.pinterest.viewContent(product);
    window.adsTracking.snapchat.viewContent(product);
    window.adsTracking.newsbreak.viewContent(product);
  } catch (error) {
    console.log('error: ', error);
  }
}

async function getAdsTracking() {
  try {
    const params = new URLSearchParams();
    params.append('shop', window.Shopify?.shop || '');
    params.append('storeType', 'SHOPIFY');
    if (window.meta?.page?.pageType === 'product') {
      params.append('productId', String(window.meta?.product?.id || ''));
    }
    const response = await fetch(
      `${window.BASE_URL}/storefront/stores/pixels?${params.toString()}`
    );
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    const facebookPixels = data?.data?.facebookPixels || [];
    const googleAds = data?.data?.googleAds || [];
    const googleAnalytics = data?.data?.googleAnalytics || [];
    const newsbreakPixels = data?.data?.newsbreakPixels || [];
    const pinterestTags = data?.data?.pinterestTags || [];
    const snapchatPixels = data?.data?.snapchatPixels || [];
    const tiktokPixels = data?.data?.tiktokPixels || [];
    if (facebookPixels.length) {
      await window.adsTracking.facebook.initPixels(facebookPixels);
    }
    if (googleAds.length || googleAnalytics.length) {
      await window.adsTracking.google.init(googleAnalytics, googleAds);
    }
    if (newsbreakPixels.length) {
      window.adsTracking.newsbreak.init(newsbreakPixels);
    }
    if (pinterestTags.length) {
      window.adsTracking.pinterest.init(pinterestTags);
    }
    if (snapchatPixels.length) {
      window.adsTracking.snapchat.init(snapchatPixels);
    }
    if (tiktokPixels.length) {
      window.adsTracking.tiktok.init(tiktokPixels);
    }
  } catch (error) {
    console.log('error: ', error);
  }
}

function getRefQueryParam(name) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(location.search);
  return results === null || results[1] === undefined
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

function saveTrackingData() {
  if (!localStorage.getItem('track_first')) {
    localStorage.setItem('track_ref', document.referrer || 'none');
    localStorage.setItem('track_first', window.location.href);
    const utm_source = getRefQueryParam('utm_source');
    const utm_medium = getRefQueryParam('utm_medium');
    const utm_content = getRefQueryParam('utm_content');
    const utm_campaign = getRefQueryParam('utm_campaign');
    const utm_term = getRefQueryParam('utm_term');
    const utm_id = getRefQueryParam('utm_id');
    if (utm_source) localStorage.setItem('utm_source', utm_source);
    if (utm_medium) localStorage.setItem('utm_medium', utm_medium);
    if (utm_campaign) localStorage.setItem('utm_campaign', utm_campaign);
    if (utm_content) localStorage.setItem('utm_content', utm_content);
    if (utm_term) localStorage.setItem('utm_term', utm_term);
    if (utm_id) localStorage.setItem('utm_id', utm_id);
  }
}
function extractTrafficSourceFromReferer(referer) {
  const TRAFFIC_SOURCES = [
    'facebook.com',
    'direct',
    'tiktok.com',
    'yandex',
    'trustpilot',
    'mail.google.com',
    'google.com',
    'apps.admob.com',
    't.co',
    'base.vn',
    'mail.yahoo.com',
    'yahoo.com',
    'youtube.com',
    'messenger.com',
    'instagram.com',
    'lavasoft.gosearchresults.com',
    'klaviyo.com',
    'ecosia.org',
    'duckduckgo',
    'bing.com',
    'baidu.com',
  ];
  for (const source of TRAFFIC_SOURCES) {
    if (referer.includes(source)) {
      return source;
    }
  }
  return 'direct';
}
function getTrackingData() {
  const data = {
    utmParams: {
      source: '',
      medium: '',
      campainId: '', // Lưu ý: Có thể là typo của "campaignId"
      utm_content: '',
      utm_term: '',
      utm_id: '',
    },
    conversion: {
      firstPage: '',
      refererSite: '',
      source: '',
    },
    adbCheckout: {
      reference_method: 'normal',
      abdCartId: '', // Lưu ý: Có thể là typo của "adbCartId"
    },
  };
  // 1. Chỉ đọc các thông tin tracking khi track_ref tồn tại
  const trackRef = localStorage.getItem('track_ref');
  if (trackRef) {
    data.conversion = {
      firstPage: localStorage.getItem('track_first') || '',
      refererSite: trackRef,
      source: extractTrafficSourceFromReferer(trackRef),
    };
  }
  // 2. Chỉ đọc các tham số UTM khác khi utm_source tồn tại
  const utmSource = localStorage.getItem('utm_source');
  if (utmSource) {
    data.utmParams = {
      source: utmSource,
      medium: localStorage.getItem('utm_medium') || '',
      campainId: localStorage.getItem('utm_campaign') || '',
      utm_content: localStorage.getItem('utm_content') || '',
      utm_term: localStorage.getItem('utm_term') || '',
      utm_id: localStorage.getItem('utm_id') || '',
    };
  }
  return data;
}

window.adsTracking = {
  facebook: new FacebookPixel(),
  google: new GoogleAds(),
  tiktok: new TiktokPixel(),
  pinterest: new Pinterest(),
  snapchat: new SnapChatPixel(),
  newsbreak: new NewsbreakPixel(),
};
window.checkout = new Checkout();
window.visitor = new Visitor();
(async function () {
  await window.visitor.getVisitor();
  await getAdsTracking();
  await postAction();
  if (window.meta?.page?.pageType === 'product') {
    await postActionProductPage();
  }
  saveTrackingData();
})();
